/**
 * History page component (T159).
 *
 * Displays trade history and agent activity with:
 * - Stats cards (Total Trades, Volume, P&L, Win Rate)
 * - Tabbed view for Trade History and Agent Activity
 * - Filters and pagination
 */
import { useCallback, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { AlertCircle, Download } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { TradeHistoryTable } from '@/components/history/TradeHistoryTable';
import { HistoryFilters } from '@/components/history/HistoryFilters';
import { AgentActivityLog } from '@/components/history/AgentActivityLog';
import { historyService, type TradeHistoryFilters } from '@/services/history';
import { cn, formatCurrency, formatPercent } from '@/lib/utils';

export default function History() {
  const [filters, setFilters] = useState<TradeHistoryFilters>({
    page: 1,
    pageSize: 20,
  });
  const [statsPeriod, setStatsPeriod] = useState('week');
  const [exportError, setExportError] = useState<string | null>(null);

  // Fetch trade history
  const tradesQuery = useQuery({
    queryKey: ['history', 'trades', filters],
    queryFn: () => historyService.getTrades(filters),
  });

  // Fetch period stats
  const statsQuery = useQuery({
    queryKey: ['history', 'stats', statsPeriod],
    queryFn: () => historyService.getStats(statsPeriod),
  });

  // Handle page change
  const handlePageChange = (page: number) => {
    setFilters((prev) => ({ ...prev, page }));
  };

  // Handle export
  const handleExport = useCallback(async () => {
    setExportError(null);
    try {
      const blob = await historyService.exportHistory({
        symbol: filters.symbol,
        side: filters.side,
        startDate: filters.startDate,
        endDate: filters.endDate,
      });

      // Create download link
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `trade_history_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      setTimeout(() => {
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      }, 100);
    } catch (error) {
      console.error('Failed to export history:', error);
      setExportError(
        error instanceof Error ? error.message : 'Failed to export trade history. Please try again.'
      );
    }
  }, [filters]);

  const stats = statsQuery.data;

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">History</h1>
          <p className="text-muted-foreground">View your trading history and agent activity</p>
        </div>
        <Button variant="outline" onClick={handleExport}>
          <Download className="mr-2 h-4 w-4" />
          Export
        </Button>
      </div>

      {/* Export error message */}
      {exportError && (
        <div className="flex items-center gap-2 rounded-lg border border-destructive/50 bg-destructive/10 p-3 text-sm text-destructive">
          <AlertCircle className="h-4 w-4 shrink-0" />
          <span>{exportError}</span>
          <button
            onClick={() => setExportError(null)}
            className="ml-auto text-xs underline hover:no-underline"
          >
            Dismiss
          </button>
        </div>
      )}

      {/* Stats cards */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Total Trades
            </CardTitle>
          </CardHeader>
          <CardContent>
            {statsQuery.isLoading ? (
              <div className="h-8 w-16 bg-muted animate-pulse rounded" />
            ) : (
              <p className="text-2xl font-bold">{stats?.totalTrades ?? 0}</p>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Total Volume
            </CardTitle>
          </CardHeader>
          <CardContent>
            {statsQuery.isLoading ? (
              <div className="h-8 w-24 bg-muted animate-pulse rounded" />
            ) : (
              <p className="text-2xl font-bold">{formatCurrency(stats?.totalVolume ?? 0)}</p>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Realized P&L
            </CardTitle>
          </CardHeader>
          <CardContent>
            {statsQuery.isLoading ? (
              <div className="h-8 w-20 bg-muted animate-pulse rounded" />
            ) : (
              <p
                className={cn(
                  'text-2xl font-bold',
                  (stats?.realizedPnl ?? 0) >= 0 ? 'text-primary' : 'text-destructive'
                )}
              >
                {formatCurrency(stats?.realizedPnl ?? 0)}
              </p>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Win Rate</CardTitle>
          </CardHeader>
          <CardContent>
            {statsQuery.isLoading ? (
              <div className="h-8 w-16 bg-muted animate-pulse rounded" />
            ) : (
              <p className="text-2xl font-bold">{formatPercent((stats?.winRate ?? 0) * 100)}</p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Tabs for Trade History and Agent Activity */}
      <Tabs defaultValue="trades">
        <TabsList>
          <TabsTrigger value="trades">Trade History</TabsTrigger>
          <TabsTrigger value="agent">Agent Activity</TabsTrigger>
        </TabsList>

        <TabsContent value="trades" className="space-y-4">
          {/* Filters */}
          <HistoryFilters
            filters={filters}
            onFiltersChange={setFilters}
            onPeriodChange={setStatsPeriod}
            currentPeriod={statsPeriod}
          />

          {/* Trade history table */}
          <TradeHistoryTable
            trades={tradesQuery.data?.trades ?? []}
            total={tradesQuery.data?.total ?? 0}
            page={filters.page ?? 1}
            pageSize={filters.pageSize ?? 20}
            totalPages={tradesQuery.data?.totalPages}
            onPageChange={handlePageChange}
            isLoading={tradesQuery.isLoading}
          />
        </TabsContent>

        <TabsContent value="agent">
          <AgentActivityLog />
        </TabsContent>
      </Tabs>
    </div>
  );
}
